import React, { useState, useEffect, useCallback } from 'react';
import { listingsApi } from '../lib/api';
import { formatCurrency, formatDate, PROCUREMENT_SECTORS } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Plus, MagnifyingGlass, Pencil, Trash, Storefront, Handshake,
  CurrencyDollar, Users, Eye, ChatText, Buildings, Briefcase, Sparkle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const LISTING_TYPES = [
  { id: 'sell', label: 'Sell Business', color: 'bg-blue-500' },
  { id: 'buy', label: 'Looking to Buy', color: 'bg-emerald-500' },
  { id: 'partnership', label: 'Partnership', color: 'bg-purple-500' },
  { id: 'merger', label: 'Merger', color: 'bg-amber-500' },
  { id: 'investment', label: 'Investment', color: 'bg-red-500' },
];

const LISTING_STATUSES = [
  { id: 'draft', label: 'Draft' },
  { id: 'active', label: 'Active' },
  { id: 'under_review', label: 'Under Review' },
  { id: 'closed', label: 'Closed' },
  { id: 'withdrawn', label: 'Withdrawn' },
];

export default function BusinessMarketplacePage() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isInquiryOpen, setIsInquiryOpen] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [highlightInput, setHighlightInput] = useState('');
  const [requirementInput, setRequirementInput] = useState('');
  const [valuation, setValuation] = useState(null);    // { low, high, recommended, multiple, summary, factors }
  const [valuating, setValuating] = useState(false);
  const [formData, setFormData] = useState({
    title: '', listing_type: 'sell', sector: '', description: '',
    asking_price: 0, annual_revenue: 0, annual_profit: 0,
    employee_count: 0, location: '', highlights: [], requirements: [], status: 'draft'
  });

  const requestValuation = async () => {
    if ((Number(formData.annual_revenue) || 0) <= 0 && (Number(formData.annual_profit) || 0) <= 0) {
      toast.error('Add annual revenue or annual profit first');
      return;
    }
    setValuating(true);
    try {
      const { data } = await listingsApi.estimateValuation({
        annual_revenue: Number(formData.annual_revenue) || 0,
        annual_profit:  Number(formData.annual_profit)  || 0,
        employee_count: Number(formData.employee_count) || 0,
        sector:         formData.sector || null,
        description:    formData.description || null,
        location:       formData.location || null,
        listing_type:   formData.listing_type || 'acquisition',
      });
      setValuation(data);
      toast.success('Valuation ready');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Valuation failed');
    } finally {
      setValuating(false);
    }
  };

  const applyRecommendedPrice = () => {
    if (!valuation?.recommended) return;
    setFormData((f) => ({ ...f, asking_price: Math.round(valuation.recommended) }));
    toast.success('Asking price updated');
  };

  const fetchListings = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (typeFilter) params.listing_type = typeFilter;
      if (sectorFilter) params.sector = sectorFilter;
      const response = await listingsApi.getAll(params);
      setListings(response.data);
    } catch (error) {
      toast.error('Failed to fetch listings');
    } finally { setLoading(false); }
  }, [typeFilter, sectorFilter]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingListing) {
        await listingsApi.update(editingListing.id, formData);
        toast.success('Listing updated');
      } else {
        await listingsApi.create(formData);
        toast.success('Listing created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save listing');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await listingsApi.delete(id);
      toast.success('Listing deleted');
      fetchListings();
    } catch (error) { toast.error('Delete failed'); }
  };

  const handleInquiry = async () => {
    if (!selectedListing || !inquiryMessage.trim()) return;
    try {
      await listingsApi.inquire(selectedListing.id, inquiryMessage);
      toast.success('Inquiry sent successfully');
      setIsInquiryOpen(false);
      setInquiryMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send inquiry');
    }
  };

  const openEdit = (listing) => {
    setEditingListing(listing);
    setFormData({
      title: listing.title, listing_type: listing.listing_type, sector: listing.sector || '',
      description: listing.description || '', asking_price: listing.asking_price || 0,
      annual_revenue: listing.annual_revenue || 0, annual_profit: listing.annual_profit || 0,
      employee_count: listing.employee_count || 0, location: listing.location || '',
      highlights: listing.highlights || [], requirements: listing.requirements || [], status: listing.status || 'draft'
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingListing(null);
    setFormData({
      title: '', listing_type: 'sell', sector: '', description: '',
      asking_price: 0, annual_revenue: 0, annual_profit: 0,
      employee_count: 0, location: '', highlights: [], requirements: [], status: 'draft'
    });
    setHighlightInput(''); setRequirementInput('');
    setValuation(null);
  };

  const filteredListings = listings.filter(l =>
    !search || l.title?.toLowerCase().includes(search.toLowerCase()) || l.description?.toLowerCase().includes(search.toLowerCase())
  );

  const myListings = filteredListings.filter(l => l.owner_id === user?.id);
  const allListings = filteredListings.filter(l => l.status === 'active');

  const getTypeBadge = (type) => {
    const config = LISTING_TYPES.find(t => t.id === type);
    return <Badge className={`${config?.color || 'bg-zinc-500'} text-white`}>{config?.label || type}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Business Marketplace</h1>
          <p className="text-muted-foreground text-sm mt-1">Build, buy, sell & partner with businesses</p>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="rounded-sm" data-testid="create-listing-btn">
          <Plus size={18} className="mr-2" /> Create Listing
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input placeholder="Search listings..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-sm" data-testid="search-listings" />
        </div>
        <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {LISTING_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sectorFilter || "all"} onValueChange={(v) => setSectorFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="All Sectors" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sectors</SelectItem>
            {PROCUREMENT_SECTORS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="marketplace">
        <TabsList>
          <TabsTrigger value="marketplace"><Storefront size={16} className="mr-1" /> Marketplace</TabsTrigger>
          <TabsTrigger value="my-listings"><Briefcase size={16} className="mr-1" /> My Listings ({myListings.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="marketplace">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {loading ? <div className="col-span-full text-center p-8 text-muted-foreground">Loading...</div> :
              allListings.length === 0 ? <div className="col-span-full text-center p-12 border border-dashed border-border rounded-sm text-muted-foreground"><Storefront size={40} className="mx-auto mb-3 opacity-40" /><p>No active listings</p></div> :
              allListings.map(listing => <ListingCard key={listing.id} listing={listing} onInquire={(l) => { setSelectedListing(l); setIsInquiryOpen(true); }} getTypeBadge={getTypeBadge} isOwner={listing.owner_id === user?.id} />)}
          </div>
        </TabsContent>

        <TabsContent value="my-listings">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {myListings.length === 0 ? <div className="col-span-full text-center p-12 border border-dashed border-border rounded-sm text-muted-foreground"><p>No listings created</p></div> :
              myListings.map(listing => (
                <ListingCard key={listing.id} listing={listing} onEdit={() => openEdit(listing)} onDelete={() => handleDelete(listing.id)} getTypeBadge={getTypeBadge} isOwner={true} showActions />
              ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingListing ? 'Edit Listing' : 'Create Listing'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required data-testid="listing-title" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select value={formData.listing_type} onValueChange={(v) => setFormData({ ...formData, listing_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LISTING_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LISTING_STATUSES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sector</Label>
                <Select value={formData.sector || "none"} onValueChange={(v) => setFormData({ ...formData, sector: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{PROCUREMENT_SECTORS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Asking Price</Label>
                <Input type="number" min="0" value={formData.asking_price} onChange={(e) => setFormData({ ...formData, asking_price: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Annual Revenue</Label>
                <Input type="number" min="0" value={formData.annual_revenue} onChange={(e) => setFormData({ ...formData, annual_revenue: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Annual Profit</Label>
                <Input type="number" min="0" value={formData.annual_profit} onChange={(e) => setFormData({ ...formData, annual_profit: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Employees</Label>
              <Input type="number" min="0" value={formData.employee_count} onChange={(e) => setFormData({ ...formData, employee_count: parseInt(e.target.value) || 0 })} />
            </div>

            {/* ── AI Valuation Estimator ─────────────────────────────────── */}
            <div className="rounded-sm border border-[#002677]/25 bg-gradient-to-br from-[#002677]/[0.04] to-transparent p-4 space-y-3" data-testid="ai-valuation-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-sm bg-[#002677] flex items-center justify-center flex-shrink-0">
                    <Sparkle size={14} weight="fill" color="#ffffff" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold leading-tight">AI valuation estimator</div>
                    <div className="text-[11px] text-muted-foreground leading-tight">
                      GPT-5.2 estimates a ballpark range from revenue, profit, sector & headcount.
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={requestValuation}
                  disabled={valuating}
                  className="rounded-sm h-8 px-3 text-xs uppercase tracking-wider font-semibold"
                  data-testid="ai-valuation-estimate-btn"
                >
                  {valuating ? 'Estimating…' : (valuation ? 'Re-estimate' : 'Estimate value')}
                </Button>
              </div>

              {valuation && (
                <div className="space-y-3" data-testid="ai-valuation-result">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-sm border border-border bg-background p-2.5 text-center">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Low</div>
                      <div className="text-sm font-bold mt-0.5" data-testid="ai-valuation-low">{formatCurrency(valuation.low)}</div>
                    </div>
                    <div className="rounded-sm border border-[#002677] bg-[#002677] text-white p-2.5 text-center">
                      <div className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">Recommended</div>
                      <div className="text-sm font-bold mt-0.5" data-testid="ai-valuation-recommended">{formatCurrency(valuation.recommended)}</div>
                    </div>
                    <div className="rounded-sm border border-border bg-background p-2.5 text-center">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">High</div>
                      <div className="text-sm font-bold mt-0.5" data-testid="ai-valuation-high">{formatCurrency(valuation.high)}</div>
                    </div>
                  </div>
                  {valuation.multiple && (
                    <div className="text-[11px] text-muted-foreground">
                      <span className="font-semibold text-foreground">Multiple:</span> {valuation.multiple}
                    </div>
                  )}
                  {valuation.summary && (
                    <p className="text-xs leading-relaxed text-muted-foreground" data-testid="ai-valuation-summary">
                      {valuation.summary}
                    </p>
                  )}
                  {Array.isArray(valuation.factors) && valuation.factors.length > 0 && (
                    <ul className="text-[11px] space-y-1">
                      {valuation.factors.map((f, i) => (
                        <li key={i} className="flex gap-1.5">
                          <span className="text-[#002677] flex-shrink-0">•</span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex items-center justify-between pt-1 border-t border-border/50">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Estimate only · verify with an M&A advisor
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      onClick={applyRecommendedPrice}
                      className="h-7 px-2 text-[11px] rounded-sm bg-[#002677] hover:bg-[#002677]/90"
                      data-testid="ai-valuation-apply-btn"
                    >
                      Use as asking price
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Key Highlights</Label>
              <div className="flex gap-2">
                <Input value={highlightInput} onChange={(e) => setHighlightInput(e.target.value)} placeholder="Add highlight" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (highlightInput.trim()) { setFormData({ ...formData, highlights: [...formData.highlights, highlightInput.trim()] }); setHighlightInput(''); } } }} />
                <Button type="button" variant="outline" onClick={() => { if (highlightInput.trim()) { setFormData({ ...formData, highlights: [...formData.highlights, highlightInput.trim()] }); setHighlightInput(''); } }}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1">{formData.highlights.map((h, i) => <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setFormData({ ...formData, highlights: formData.highlights.filter((_, j) => j !== i) })}>{h} x</Badge>)}</div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="listing-submit">{editingListing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inquiry Dialog */}
      <Dialog open={isInquiryOpen} onOpenChange={setIsInquiryOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Inquiry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedListing && (
              <div className="p-3 bg-muted rounded-sm">
                <p className="font-medium">{selectedListing.title}</p>
                <p className="text-sm text-muted-foreground">{formatCurrency(selectedListing.asking_price)}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Your Message *</Label>
              <Textarea value={inquiryMessage} onChange={(e) => setInquiryMessage(e.target.value)} rows={4} placeholder="Introduce yourself and express your interest..." data-testid="inquiry-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsInquiryOpen(false)}>Cancel</Button>
            <Button onClick={handleInquiry} disabled={!inquiryMessage.trim()} data-testid="send-inquiry-btn">Send Inquiry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ListingCard({ listing, onInquire, onEdit, onDelete, getTypeBadge, isOwner, showActions }) {
  return (
    <div className="bg-card border border-border p-5 rounded-sm hover:-translate-y-[1px] transition-transform" data-testid={`listing-${listing.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          {getTypeBadge(listing.listing_type)}
          <h3 className="font-heading font-semibold mt-2">{listing.title}</h3>
          {listing.owner_name && <p className="text-xs text-muted-foreground">by {listing.owner_name}</p>}
        </div>
        {showActions && (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit}><Pencil size={14} /></Button>
            <Button variant="ghost" size="sm" className="text-destructive" onClick={onDelete}><Trash size={14} /></Button>
          </div>
        )}
      </div>
      {listing.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{listing.description}</p>}
      {listing.sector && <Badge variant="outline" className="mb-3 text-xs">{PROCUREMENT_SECTORS.find(s => s.id === listing.sector)?.label || listing.sector}</Badge>}

      <div className="grid grid-cols-2 gap-3 text-sm mb-3">
        {listing.asking_price > 0 && (
          <div className="flex items-center gap-1"><CurrencyDollar size={14} className="text-muted-foreground" /><span className="font-semibold">{formatCurrency(listing.asking_price)}</span></div>
        )}
        {listing.annual_revenue > 0 && (
          <div><span className="text-xs text-muted-foreground">Revenue: </span><span className="font-medium">{formatCurrency(listing.annual_revenue)}</span></div>
        )}
        {listing.employee_count > 0 && (
          <div className="flex items-center gap-1"><Users size={14} className="text-muted-foreground" /><span>{listing.employee_count} employees</span></div>
        )}
        {listing.location && (
          <div className="text-muted-foreground text-xs">{listing.location}</div>
        )}
      </div>

      {listing.highlights?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">{listing.highlights.slice(0, 3).map((h, i) => <Badge key={i} variant="secondary" className="text-xs">{h}</Badge>)}</div>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Eye size={12} />{listing.views || 0} views</span>
          <span className="flex items-center gap-1"><ChatText size={12} />{listing.inquiries || 0} inquiries</span>
        </div>
        {!isOwner && onInquire && (
          <Button size="sm" variant="outline" onClick={() => onInquire(listing)} data-testid={`inquire-${listing.id}`}>
            <Handshake size={14} className="mr-1" /> Inquire
          </Button>
        )}
      </div>
    </div>
  );
}
